/**
 * Jeden schemat zdarzeń dla wszystkich CTA konwersyjnych w serwisie.
 *
 * 🔴 Powód istnienia tego pliku: klasa `lead_umow_sie` stoi na 128 przyciskach
 * w 57 plikach. Każde „Umów się" — w nagłówku, w hero, w cenniku, w bloku
 * domykającym artykułu, w pasku dolnym — wysyłało do GA4 sygnał nie do
 * odróżnienia od pozostałych. Pytanie „czy ludzie nie klikają, bo przycisk
 * jest za nisko" nie miało jak dostać odpowiedzi.
 *
 * Klas NIE ruszamy: istniejące reguły w GTM na nich stoją i mają dalej działać.
 * Dokładamy obok drugą warstwę — `dataLayer` z parametrami.
 *
 * Pełna specyfikacja dla osoby konfigurującej GTM: docs/cta-analytics-events.md
 */

import {
  GTM_GIFT_BUY_NOW_CLICK,
  GTM_INIT_CHECKOUT_UMOW_SIE,
  GTM_LEAD_GIFT_MAINPAGE,
  GTM_LEAD_GIFT_NAVBAR,
  GTM_LEAD_GIFT_PRICELIST,
  GTM_LEAD_UMOW_SIE,
  GTM_LEAD_UMOW_SIE_ONLINE,
  GTM_PHONE_CONTACT_PAGE,
  GTM_PHONE_FOOTER,
  GTM_PHONE_FOOTER_STICKY_MOBILE,
  GTM_PHONE_LANDING_PAGE_CONTACT_SECTION,
  GTM_PHONE_NAVIGATION_TOP_BAR,
} from "./gtm_classes";

export const CTA_VIEW = "cta_view";
export const CTA_CLICK = "cta_click";

// Rodzaj akcji — grupuje powierzchnie w lejki. Telefon i formularz to dwie
// różne drogi do tej samej wizyty i mieszanie ich zaciera obraz.
const TYPY: Array<[string, string]> = [
  [GTM_LEAD_UMOW_SIE_ONLINE, "booking_online"],
  [GTM_INIT_CHECKOUT_UMOW_SIE, "booking_online"],
  [GTM_LEAD_UMOW_SIE, "booking"],
  [GTM_PHONE_FOOTER_STICKY_MOBILE, "phone"],
  [GTM_PHONE_NAVIGATION_TOP_BAR, "phone"],
  [GTM_PHONE_LANDING_PAGE_CONTACT_SECTION, "phone"],
  [GTM_PHONE_CONTACT_PAGE, "phone"],
  [GTM_PHONE_FOOTER, "phone"],
  [GTM_LEAD_GIFT_NAVBAR, "gift"],
  [GTM_LEAD_GIFT_PRICELIST, "gift"],
  [GTM_LEAD_GIFT_MAINPAGE, "gift"],
  [GTM_GIFT_BUY_NOW_CLICK, "gift"],
];

export const CTA_SELECTOR = TYPY.map(([klasa]) => `.${klasa}`).join(",");

/**
 * Powierzchnia — GDZIE na stronie stoi przycisk.
 *
 * Kolejność ma znaczenie: pierwszy pasujący przodek wygrywa, więc wpisy węższe
 * (karta w artykule) muszą stać przed szerszymi (kolumna treści).
 *
 * ⚠ To jest mapa po klasach CSS, więc zmiana nazwy kontenera rozłącza pomiar.
 * Dlatego brak trafienia NIE jest cichy — leci `unmapped` razem z klasami
 * przodka, żeby dziura była widoczna w GA4 zamiast udawać zero kliknięć.
 */
const POWIERZCHNIE: Array<[string, string]> = [
  // artykuły i blog — te bloki mają własne atrybuty, ale trzymamy też selektory
  // jako siatkę bezpieczeństwa, gdyby atrybut gdzieś wypadł
  [".ac_bar", "article_sticky_bar"],
  [".ac_promo", "article_sidebar_promo"],
  ['[data-article-card="stats"]', "article_stats_card"],
  ['[data-article-card="closing"]', "article_closing"],
  [".more-wrapper--lead-umow", "article_lead_card"],
  [".more-wrapper", "article_more_card"],
  [".article_content", "article_inline"],

  // nawigacja i stopka — obecne na każdej stronie
  [".navigation-contact", "header_phone"],
  [".navigation-items", "header_gift"],
  [".navigation", "header"],
  [".call_cta", "sticky_call_bar"],
  [".footer_contact_phone_wrapper", "footer_phone"],
  [".footer", "footer"],

  // landingi miejskie i strona główna
  [".cl_hero_actions", "hero"],
  [".cl_book_card", "online_booking"],
  [".cl_head_action", "price_accordion"],
  [".cl_row_action", "price_accordion"],
  [".cl_price_banner", "gift_banner"],
  [".cl_ctabar_card", "cta_bar_phone"],
  [".cl_ctabar", "cta_bar"],
  [".cl_flow_head", "exam_process"],
  [".cl_rev_close", "reviews"],
  [".cl_content_actions", "content_section"],
  [".home_melanoma_left_column", "melanoma"],
  [".call_to_action_wrapper", "cta_section"],
  [".quiz", "quiz"],

  // Domknięcie po pierwszym przebiegu: te trzy kontenery zgłaszały się jako
  // `unmapped` na stronie głównej, landingach, bonach i kontakcie. Dokładnie
  // po to ten mechanizm nie milczy przy braku trafienia.
  [".alert", "alert_box"],
  [".gift_card_cities_list_wrapper", "gift_city_list"],
  [".registration_wrappper", "contact_page"],
];

const TEKST_MAX = 60;

const typDla = (el: Element): string => {
  const trafienie = TYPY.find(([klasa]) => el.classList.contains(klasa));
  return trafienie ? trafienie[1] : "other";
};

// Klasy, które nic nie mówią o sekcji: opakowanie z `Button.tsx`, warianty
// wyglądu, siatka. Bez ich odsiania podpowiedź przy `unmapped` brzmiała zawsze
// „button  button-primary button-L" — bo CTA bywa <a> WEWNĄTRZ <span> Buttona,
// więc najbliższy przodek to zawsze ten sam wrapper. Trzeba było wchodzić
// w DOM produkcji, żeby ustalić, której sekcji brakuje.
const KLASY_BEZ_TRESCI = new Set([
  "button", "buttons", "button-primary", "button-secondary", "button-white",
  "button-L", "button-M", "button-S", "btn-appt-now",
  "container", "row", "col", "wrapper", "inner", "active", "open",
]);

const KLASY_TYPU = new Set(TYPY.map(([klasa]) => klasa));

const HINT_MAX = 90;

/**
 * Łańcuch najbliższych przodków niosących nazwę — do diagnozy braku mapowania.
 *
 * Trzy człony wystarczą, żeby wskazać sekcję bez otwierania przeglądarki,
 * a mieszczą się w limicie długości parametru GA4. Klasy GTM odsiewamy, bo
 * powtarzają to, co już jest w `cta_type`.
 */
const podpowiedzDla = (el: Element): string => {
  const czlony: string[] = [];
  let wezel = el.parentElement;
  let poziom = 0;
  while (wezel && poziom < 8 && czlony.length < 3) {
    const nazwa = (wezel.className || "").toString().trim();
    const istotne = nazwa
      .split(/\s+/)
      .filter((k) => k && !KLASY_BEZ_TRESCI.has(k) && !KLASY_TYPU.has(k));
    if (istotne.length) czlony.push(istotne[0]);
    wezel = wezel.parentElement;
    poziom += 1;
  }
  return czlony.join(" < ").slice(0, HINT_MAX);
};

const powierzchniaDla = (el: Element): { surface: string; hint: string } => {
  // Atrybut bije mapę selektorów: stoi przy komponencie, więc nie rozjeżdża
  // się przy zmianie nazwy klasy.
  const jawna = el.closest("[data-cta-surface]") as HTMLElement | null;
  if (jawna) return { surface: jawna.dataset.ctaSurface || "unmapped", hint: "" };

  for (const [selektor, nazwa] of POWIERZCHNIE) {
    if (el.closest(selektor)) return { surface: nazwa, hint: "" };
  }

  // Nic nie pasuje — zamiast milczeć, oddajemy ścieżkę przodków, żeby dało się
  // dopisać brakujący wpis zamiast zgadywać.
  return { surface: "unmapped", hint: podpowiedzDla(el) };
};

const szablonStrony = (): string => {
  if (typeof document === "undefined") return "unknown";
  const typ = document.body.getAttribute("type");
  if (typ) return typ; // "blog" albo "article" — ustawiane przez szablony
  const sciezka = window.location.pathname;
  if (sciezka === "/" ) return "home";
  if (sciezka.startsWith("/cennik")) return "cennik";
  if (sciezka.startsWith("/o-nas")) return "o-nas";
  if (/^\/[a-z-]+\/(dermatoskopowe|usuwanie|laserowe|konsultacje)/.test(sciezka)) return "city-landing";
  return "other";
};

// Miasto z adresu landingu miejskiego — pozwala porównać te same powierzchnie
// między ośrodkami, czyli to, o co chodziło od początku.
const miastoZeSciezki = (): string => {
  const m = window.location.pathname.match(
    /^\/(warszawa|poznan|krakow|gdansk|wroclaw|szczecin|katowice|lublin|lodz|gdynia|bydgoszcz|zywiec)\//
  );
  return m ? m[1] : "";
};

export const opiszCta = (el: Element) => {
  const { surface, hint } = powierzchniaDla(el);
  const cel = (el as HTMLAnchorElement).href || el.closest("a")?.getAttribute("href") || "";
  const miasto =
    (el.closest("[data-cta-city]") as HTMLElement | null)?.dataset.ctaCity || miastoZeSciezki();

  return {
    cta_surface: surface,
    cta_type: typDla(el),
    cta_label: (el.textContent || "").trim().replace(/\s+/g, " ").slice(0, TEKST_MAX),
    cta_target: cel,
    cta_city: miasto,
    page_template: szablonStrony(),
    // Szerokość, nie „urządzenie": o tym, czy widać pasek dolny czy planszę
    // boczną, decyduje u nas breakpoint 768px, a nie klasa sprzętu z GA4.
    viewport: typeof window !== "undefined" && window.innerWidth <= 768 ? "mobile" : "desktop",
    // 🔴 ZAWSZE obecny, pusty gdy powierzchnia rozpoznana — nigdy warunkowo.
    // GTM trzyma jeden model danych na odsłonę i SCALA kolejne pushe: klucz
    // nieobecny w bieżącym zdarzeniu zachowuje wartość z poprzedniego. Przy
    // wersji warunkowej zmienna `DLV - cta_unmapped_parent` doklejała
    // nieaktualną podpowiedź do zdarzeń rozpoznanych — 313 z 448 niepustych
    // wartości w GA4 z 2026-08-03 siedziało na zmapowanych powierzchniach
    // (`hero` z podpowiedzią `lq_ctas` z quizu). Licznik nierozpoznanych
    // zawyżał się ~3× i nie dało się na nim polegać. `cta_city` obok radzi
    // sobie tak samo: zawsze jest, pusty gdy nie dotyczy.
    cta_unmapped_parent: hint,
    // Promo-modal fields (components/promo/PromoModal.tsx): which trigger
    // opened the dialog, the A/B arm, the discount code shown, and seconds
    // open at click/close. Sent empty from every other CTA for the same
    // reason as cta_unmapped_parent: GTM merges pushes, so a key left out
    // here would carry the modal's last value into the next booking click.
    reason: "",
    variant: "",
    code: "",
    value: 0,
  };
};

export const trackCta = (event: string, params: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  // page_path w każdym zdarzeniu: wbudowana zmienna GTM potrafi się
  // zdezaktualizować przy nawigacji po stronie bez przeładowania.
  w.dataLayer.push({ event, page_path: window.location.pathname, ...params });
};

// Moving around the price list is not a conversion, so it stays out of
// cta_click: a booking report must not count a switched tab as a booking intent.
export const PRICE_INTERACTION = "price_interaction";

/**
 * A patient chose a category in the price list or opened the quality notes.
 *
 * `cta_surface` says which control (`price_tabs`, `price_chips`, `price_others`,
 * `price_proof`) and `cta_label` which category. Sent with the full key set for
 * the reason given at `cta_unmapped_parent`: GTM merges pushes. No new GA4
 * dimension is needed, the property has none left.
 */
export const trackPriceInteraction = (surface: string, categoryId: string) => {
  if (typeof window === "undefined") return;
  trackCta(PRICE_INTERACTION, {
    cta_surface: surface,
    cta_type: "price_navigation",
    cta_label: categoryId,
    cta_target: "",
    cta_city: miastoZeSciezki(),
    page_template: szablonStrony(),
    viewport: window.innerWidth <= 768 ? "mobile" : "desktop",
    cta_unmapped_parent: "",
    reason: "",
    variant: "",
    code: "",
    value: 0,
  });
  // Lets Clarity recordings be filtered by the category the patient looked at.
  // Clarity loads through GTM after consent; before that this is simply skipped.
  try {
    (window as any).clarity?.("set", "price_category", categoryId);
  } catch {
    /* Clarity absent or blocked */
  }
};
